import { useState } from "react";
import { CreditCard, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ListShell } from "@/components/shared/ListShell";
import { formatCurrency } from "@/utils/format";
import {
  useSubscriptionPlans,
  useDeletePlan,
  type SubscriptionPlanRow,
} from "@/hooks/useSubscriptionPlans";
import { PlanEditDialog } from "./PlanEditDialog";

export const PlansSection = () => {
  const { data, isLoading, error, refetch } = useSubscriptionPlans();
  const deletePlan = useDeletePlan();
  const plans = data?.data ?? [];

  const [editing, setEditing] = useState<SubscriptionPlanRow | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (plan: SubscriptionPlanRow) => {
    setEditing(plan);
    setDialogOpen(true);
  };

  const onDelete = (plan: SubscriptionPlanRow) => {
    if (
      !window.confirm(
        `Delete ${plan.name}? This only works if no subscriptions exist on it.`,
      )
    ) {
      return;
    }
    deletePlan.mutate(plan.id, {
      onSuccess: () => toast.success("Plan deleted"),
      onError: (e) => toast.error(e.message),
    });
  };

  return (
    <section className="rounded-card border border-border bg-card">
      <div className="p-5 pb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Plans</h2>
          <p className="text-sm text-muted-foreground">
            Click a plan to edit its name, pricing, and included features.
          </p>
        </div>
        <Button onClick={openCreate} className="shrink-0">
          <Plus className="w-4 h-4 mr-1.5" /> New plan
        </Button>
      </div>

      <div className="px-5 pb-5">
        <ListShell
          isLoading={isLoading}
          error={error}
          isEmpty={plans.length === 0}
          onRetry={refetch}
          emptyTitle="No plans yet"
          emptyDescription="Create a plan to start onboarding subscribers."
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                onEdit={() => openEdit(p)}
                onDelete={() => onDelete(p)}
              />
            ))}
          </div>
        </ListShell>
      </div>

      <PlanEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        plan={editing}
      />
    </section>
  );
};

const PlanCard = ({
  plan,
  onEdit,
  onDelete,
}: {
  plan: SubscriptionPlanRow;
  onEdit: () => void;
  onDelete: () => void;
}) => (
  <div className="rounded-card border border-border p-5 flex flex-col">
    <div className="flex items-center gap-2 mb-2">
      <CreditCard className="w-5 h-5 text-primary" />
      <h3 className="font-semibold">{plan.name}</h3>
      {plan.isDefault && (
        <span className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary px-1.5 py-0.5 rounded">
          default
        </span>
      )}
      {!plan.isActive && (
        <span className="text-[10px] uppercase tracking-wide bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
          inactive
        </span>
      )}
    </div>
    <p className="text-2xl font-bold">
      {formatCurrency(Number(plan.priceMonthly))}
    </p>
    <p className="text-xs text-muted-foreground mt-1">
      per month
      {plan.priceYearly
        ? ` · ${formatCurrency(Number(plan.priceYearly))}/year`
        : ""}
    </p>
    {plan.description ? (
      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
        {plan.description}
      </p>
    ) : null}
    <p className="text-sm text-muted-foreground mt-3">
      {plan.maxBusinesses == null
        ? "Unlimited businesses"
        : `Up to ${plan.maxBusinesses} business(es)`}
    </p>
    <p className="text-xs text-muted-foreground mt-1">
      {plan.features.length} feature(s) · code {plan.code}
    </p>

    <div className="mt-auto pt-4 flex gap-2">
      <Button size="sm" variant="outline" onClick={onEdit} className="flex-1">
        <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={onDelete}
        className="text-destructive hover:text-destructive"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  </div>
);

export default PlansSection;
